/**
 * Microsite Approvals (pending pages).
 * Organisations: partner filter API; microsites: org-scoped pending list API.
 */

import userServiceApi from "./userServiceApi";
import { getRequest, postRequest, deleteRequest } from "./http";

export const PARTNER_SCOPE = {
  MEMBERS: "members",
  LEADS: "leads",
};

const USE_MOCK = false;

export const MOCK_ORGANISATIONS = {
  [PARTNER_SCOPE.MEMBERS]: [
    { id: "mem-org-1", name: "Acme Community Hub" },
    { id: "mem-org-2", name: "Bright Futures CIC" },
    { id: "mem-org-3", name: "Northside Youth Trust" },
  ],
  [PARTNER_SCOPE.LEADS]: [
    { id: "lead-org-1", name: "Greenfield Partners" },
    { id: "lead-org-2", name: "Summit Skills Ltd" },
    { id: "lead-org-3", name: "Riverbank Academy" },
  ],
};

const micrositeItem = (item) => ({ ...item });

/** Mock microsites per organisation (API response item shape) */
export const MOCK_MICROSITES_BY_ORG = {
  "mem-org-1": [
    micrositeItem({
      id: "26122409-fef9-411c-8ccf-2329062efa55",
      name: "str",
      websiteLink: "www.google.com",
      isPublished: false,
      isPublishedOnce: false,
      approvalStatus: "PENDING",
      updatedOn: "2026-10-21T08:50:05.047402",
    }),
    micrositeItem({
      id: "7fe4b968-434c-400c-9162-dd676e87689e",
      name: "ouy",
      websiteLink: "www.google.com",
      isPublished: false,
      isPublishedOnce: false,
      approvalStatus: "PENDING",
      updatedOn: "2026-10-21T08:47:05.179283",
    }),
  ],
  "mem-org-2": [
    micrositeItem({
      id: "3c2125bd-0a78-4996-99eb-ea2a48f34c74",
      name: "New site app testing",
      websiteLink: "https://www.amozon.in",
      isPublished: false,
      isPublishedOnce: false,
      approvalStatus: "PENDING",
      updatedOn: "2026-10-20T12:41:45.868996",
    }),
  ],
  "mem-org-3": [
    micrositeItem({
      id: "84ac4729-e66e-4338-acaa-1303bef9191c",
      name: "New Site Added",
      websiteLink: "https://www.professionalhygiene.co.uk",
      isPublished: true,
      isPublishedOnce: true,
      approvalStatus: "PENDING",
      updatedOn: "2026-10-20T12:36:48.45016",
    }),
  ],
  "lead-org-1": [
    micrositeItem({
      id: "lead-ms-1",
      name: "Greenfield Careers",
      websiteLink: "www.example.com",
      isPublished: false,
      isPublishedOnce: false,
      approvalStatus: "PENDING",
      updatedOn: "2026-10-19T10:00:00.000Z",
    }),
  ],
  "lead-org-2": [
    micrositeItem({
      id: "lead-ms-2",
      name: "Summit Skills Hub",
      websiteLink: "www.summit.example",
      isPublished: false,
      isPublishedOnce: false,
      approvalStatus: "PENDING",
      updatedOn: "2026-10-19T11:00:00.000Z",
    }),
  ],
  "lead-org-3": [
    micrositeItem({
      id: "lead-ms-3",
      name: "Riverbank Academy Site",
      websiteLink: "www.riverbank.example",
      isPublished: true,
      isPublishedOnce: true,
      approvalStatus: "PENDING",
      updatedOn: "2026-10-19T12:00:00.000Z",
    }),
  ],
};

const delay = (ms = 300) => new Promise((resolve) => setTimeout(resolve, ms));

const ORG_LIST_PAGE_SIZE = 10;

/**
 * Organisations for microsite approvals accordion (partner filter API).
 * @param {"members"|"leads"} partnerScope
 * @param {number} pageNumber
 * @param {number} pageSize
 */
export const getPendingMicrositeOrganisations = async (
  partnerScope,
  pageNumber = 0,
  pageSize = ORG_LIST_PAGE_SIZE
) => {
  if (USE_MOCK) {
    await delay();
    const list = [...(MOCK_ORGANISATIONS[partnerScope] || [])];
    const start = pageNumber * pageSize;
    const result = list.slice(start, start + pageSize);
    return {
      totalCount: list.length,
      pageSize,
      pageNumber,
      result,
    };
  }

  const url =
    partnerScope === PARTNER_SCOPE.LEADS
      ? userServiceApi.getAllFilterHubOrganizations(
          pageSize,
          pageNumber,
          null,
          null,
          null,
          null,
          null,
          null,
          null,
          null,
          null,
          null,
          null,
          null
        )
      : userServiceApi.getAllFilterOrganizations(
          pageSize,
          pageNumber,
          null,
          null,
          null,
          null,
          null,
          null,
          null,
          null,
          null,
          null,
          null,
          null
        );

  const response = await getRequest(url);
  const data = response?.data;
  const filteredData = data?.result?.filter(
    (item) => item.numberOfPendingApprovalMicrosites > 0
  );
  const result = (filteredData || []).map((org) => ({
    id: org.id,
    name: org.name,
    pendingMicrositeCount: org.numberOfPendingApprovalMicrosites ?? 0,
  }));

  return {
    // totalCount: data?.totalCount ?? result.length,
    totalCount: result?.length,
    pageSize: data?.pageSize ?? pageSize,
    pageNumber: data?.pageNumber ?? pageNumber,
    result,
  };
};

/**
 * Group flat pending microsite list by organisation (accordion rows).
 * @param {Array} microsites
 * @returns {{ organisations: Array<{ id, name, address, websiteLink, phone, email, pendingMicrositeCount }>, micrositesByOrg: Record<string, Array> }}
 */
export const groupPendingMicrositesByOrganization = (microsites = []) => {
  const micrositesByOrg = {};
  const orgMeta = [];

  (microsites || []).forEach((ms) => {
    // API may return org fields flat or nested under organizationDetailsDto
    const orgDetails = ms.organizationDetailsDto || {};
    const orgId = ms.organizationId || orgDetails.organizationId;
    if (!orgId) return;

    const orgName =
      ms.organizationName ||
      orgDetails.organizationName ||
      ms.name ||
      "Unknown organisation";

    if (!micrositesByOrg[orgId]) {
      micrositesByOrg[orgId] = [];
      orgMeta.push({
        id: orgId,
        name: orgName,
        address: orgDetails.addressOutputs?.[0]?.street || "",
        websiteLink: orgDetails.websiteLink || "",
        phone: orgDetails.phone || "",
        email: orgDetails.email || "",
        pendingMicrositeCount: 0,
      });
    }
    micrositesByOrg[orgId].push(ms);
  });

  orgMeta.forEach((org) => {
    org.pendingMicrositeCount = micrositesByOrg[org.id]?.length ?? 0;
  });

  return { organisations: orgMeta, micrositesByOrg };
};

/**
 * Paginated pending microsites list (/partner-admin/pending/microsites).
 * @param {"members"|"leads"} partnerScope
 */
export const getPendingMicrosites = async (
  pageNumber,
  pageSize,
  partnerScope
) => {
  if (USE_MOCK) {
    await delay();
    const scopeOrgs =
      partnerScope === PARTNER_SCOPE.LEADS
        ? ["lead-org-1", "lead-org-2", "lead-org-3"]
        : ["mem-org-1", "mem-org-2", "mem-org-3"];
    const all = scopeOrgs.flatMap((orgId) =>
      (MOCK_MICROSITES_BY_ORG[orgId] || []).map((ms) => ({
        ...ms,
        organizationId: orgId,
        organizationName:
          MOCK_ORGANISATIONS[partnerScope]?.find((o) => o.id === orgId)?.name ||
          orgId,
      }))
    );
    const start = pageNumber * pageSize;
    const result = all.slice(start, start + pageSize);
    return {
      totalCount: all.length,
      pageSize,
      pageNumber,
      result,
    };
  }

  const isActiveMember =
    partnerScope === PARTNER_SCOPE.LEADS ? false : true;

  const response = await getRequest(
    userServiceApi.getMicrositePendingApprovals(
      pageSize,
      pageNumber,
      isActiveMember
    )
  );
  return response?.data;
};

/**
 * Pending microsites for an organisation panel (table inside accordion).
 * Per-org pending microsites list (org-scoped pending/microsites API).
 */
export const getPendingMicrositesByOrganisation = async (
  organisationId,
  pageNumber,
  pageSize,
  partnerScope
) => {
  if (USE_MOCK) {
    await delay();
    const list = MOCK_MICROSITES_BY_ORG[organisationId] || [];
    const start = pageNumber * pageSize;
    const result = list.slice(start, start + pageSize);
    return {
      totalCount: list.length,
      pageSize,
      pageNumber,
      result,
    };
  }

  const isActiveMember =
    partnerScope === PARTNER_SCOPE.LEADS ? false : true;

  const response = await getRequest(
    userServiceApi.getPendingMicrositesByOrganization(
      organisationId,
      pageNumber,
      pageSize,
      isActiveMember
    )
  );
  return response?.data;
};

/**
 * Approve or deny a microsite.
 * POST /partner-admin/microsite-reviews/{micrositeId}
 * @param {string} micrositeId
 * @param {{ action: "APPROVE"|"REJECT", rejectionReason?: string }} payload
 */
export const reviewMicrosite = async (micrositeId, payload) => {
  if (USE_MOCK) {
    await delay();
    const nextStatus = payload.action === "APPROVE" ? "APPROVED" : "REJECTED";

    Object.keys(MOCK_MICROSITES_BY_ORG).forEach((orgId) => {
      MOCK_MICROSITES_BY_ORG[orgId] = MOCK_MICROSITES_BY_ORG[orgId].map((m) =>
        m.id === micrositeId ? { ...m, approvalStatus: nextStatus } : m
      );
    });

    return { success: true, micrositeId, status: nextStatus };
  }

  return postRequest(userServiceApi.getMicrositeReviews(micrositeId), {
    action: payload.action,
    rejectionReason: payload.rejectionReason ? payload.rejectionReason : "",
  });
};

/**
 * Delete a pending microsite.
 * DELETE /partner-admin/organization/microsite/{micrositeId}
 */
export const deletePendingMicrosite = async (micrositeId) => {
  if (USE_MOCK) {
    await delay();
    Object.keys(MOCK_MICROSITES_BY_ORG).forEach((orgId) => {
      MOCK_MICROSITES_BY_ORG[orgId] = MOCK_MICROSITES_BY_ORG[orgId].filter(
        (m) => m.id !== micrositeId
      );
    });
    return { success: true, micrositeId };
  }

  return deleteRequest(userServiceApi.deleteMicrosite(micrositeId));
};
